import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ShieldAlert, PowerOff, Inbox, CheckCircle2, XCircle } from "lucide-react";
import { API_BASE, api } from "./api";
import { useTenant } from "./tenant";

const Ctx = createContext({ connected: false, pendingCount: 0 });

export function RealtimeProvider({ children }) {
  const { tenantId } = useTenant();
  const [connected, setConnected] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const nav = useNavigate();
  const base = window.location.pathname.startsWith("/customer") ? "/customer" : "/admin";
  const qc = useQueryClient();
  const wsRef = useRef(null);

  const refreshPending = useCallback(() => {
    if (!tenantId) return;
    api.get(`/approvals/${tenantId}`, { params: { state: "pending" } }).then((r) => setPendingCount(r.data.length)).catch(() => {});
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    refreshPending();
    let closed = false, retry = 1000, poll = null, lastTs = new Date().toISOString();

    const invalidate = () => ["endpoints", "incidents", "dashboard", "approvals"].forEach((k) => qc.invalidateQueries({ queryKey: [k, tenantId] }));

    const handle = (msg) => {
      const d = msg.data;
      if (msg.type === "notification" && d.kind === "incident") {
        invalidate();
        toast.custom(() => (
          <Card testid="incident-toast" tone="orange" icon={ShieldAlert} kicker="New incident · real telemetry"
            title={d.title} detail={d.detail} action="Open incident →" onAction={() => nav(`${base}/incidents?open=${d.incident_id}`)} />
        ), { duration: 12000 });
      } else if (msg.type === "notification") {
        invalidate();
        const isAuto = d.kind === "auto_isolate";
        toast.custom(() => (
          <Card testid={`autonomy-toast-${d.kind}`} tone={isAuto ? "red" : "orange"} icon={isAuto ? PowerOff : ShieldAlert}
            kicker={isAuto ? "L3 Autonomous Response" : "Ransomware Engine"} title={d.title} detail={d.detail}
            action="Open incident →" onAction={() => nav(`${base}/incidents?open=${d.incident_id}`)} />
        ), { duration: 12000 });
      } else if (msg.type === "approval") {
        setPendingCount((n) => n + 1); invalidate();
        toast.custom(() => (
          <Card testid="approval-toast" tone="cyan" icon={Inbox} kicker="L2 · Awaiting approval"
            title={`${d.action.replace("_", " ")} · ${d.hostname}`} detail={d.reason}
            action="Review in inbox →" onAction={() => nav(`${base}/approvals`)} />
        ), { duration: 15000 });
      } else if (msg.type === "approval_decided") {
        refreshPending(); invalidate();
        const ok = d.status === "approved";
        toast.custom(() => (
          <Card testid="approval-decided-toast" tone={ok ? "green" : "zinc"} icon={ok ? CheckCircle2 : XCircle}
            kicker={`Proposal ${d.status} by ${d.decided_by}`} title={`${d.action.replace("_", " ")} · ${d.hostname}`} detail={d.result || d.note || ""} />
        ), { duration: 6000 });
      } else if (msg.type === "approval_escalated") {
        invalidate();
        toast.custom(() => (
          <Card testid="approval-escalated-toast" tone="red" icon={ShieldAlert} kicker="SLA breach · escalated to critical"
            title={`${d.action.replace("_", " ")} · ${d.hostname}`} detail={`Pending 15+ minutes without a decision`}
            action="Decide now →" onAction={() => nav(`${base}/approvals`)} />
        ), { duration: 20000 });
      } else if (msg.type === "endpoint") {
        qc.invalidateQueries({ queryKey: ["endpoints", tenantId] });
        if (d.new) toast.success(`AutoDiscover: ${d.hostname} enrolled via live agent`);
      }
    };

    // Fallback polling only while the socket is down.
    const startPoll = () => {
      if (poll) return;
      poll = setInterval(async () => {
        try {
          const { data } = await api.get(`/notifications/${tenantId}`, { params: { since: lastTs } });
          if (data.length) { lastTs = data[0].ts; data.reverse().forEach((n) => handle({ type: "notification", data: n })); }
          refreshPending();
        } catch (e) { console.warn("notification poll failed", e?.message); }
      }, 6000);
    };
    const stopPoll = () => { if (poll) clearInterval(poll); poll = null; };

    const connect = () => {
      if (closed) return;
      const token = localStorage.getItem("cds_token") || "";
      const url = `${API_BASE.replace(/^http/, "ws")}/ws/${tenantId}?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => { setConnected(true); stopPoll(); retry = 1000; };
      ws.onmessage = (ev) => { try { const m = JSON.parse(ev.data); if (m.type !== "hello") { lastTs = m.ts; handle(m); } } catch (e) { console.warn("ws message parse failed", e?.message); } };
      ws.onclose = () => { setConnected(false); if (closed) return; startPoll(); setTimeout(connect, retry); retry = Math.min(retry * 2, 15000); };
      ws.onerror = () => ws.close();
    };
    connect();
    return () => { closed = true; stopPoll(); wsRef.current?.close(); };
  }, [tenantId, nav, qc, refreshPending, base]);

  return <Ctx.Provider value={{ connected, pendingCount, refreshPending }}>{children}</Ctx.Provider>;
}

export const useRealtime = () => useContext(Ctx);

const TONES = {
  red: "border-red-500/60 text-red-400", orange: "border-orange-400/60 text-orange-400",
  cyan: "border-cyan-400/60 text-cyan-300", green: "border-emerald-400/60 text-emerald-400", zinc: "border-white/20 text-zinc-400",
};

const Card = ({ testid, tone, icon: Icon, kicker, title, detail, action, onAction }) => (
  <div data-testid={testid} className={`w-[360px] border p-3 bg-[#1B1B28] ${TONES[tone].split(" ")[0]}`}>
    <div className={`flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest ${TONES[tone].split(" ")[1]}`}>
      <Icon size={12} /> {kicker}
    </div>
    <div className="text-sm font-semibold mt-1 text-zinc-100">{title}</div>
    {detail && <div className="text-xs text-zinc-400">{detail}</div>}
    {action && <button onClick={onAction} className="mt-2 text-[10px] font-mono uppercase tracking-widest text-cyan-300 hover:underline">{action}</button>}
  </div>
);
